const SCORE_MAX_POINTS = 100;
const SCORE_SPEED_FACTOR = 0.01;
const SCORE_ACCURACY_FACTOR = 0.5;
const SCORE_MIN_POINTS = 10;

export function validateDecision(answerData, questionId, record, type) {
  const answer = answerData?.find(a => a.transaction_id === questionId);
  if (!answer) return false;

  if (type === 'change') {
    const drMatch = answer.dr[1] === record.optionAccountItemId && answer.dr[2] === record.changeAccountItemId;
    const crMatch = answer.cr[1] === record.optionAccountItemId && answer.cr[2] === record.changeAccountItemId;
    return drMatch || crMatch;
  }

  if (type === 'transaction') {
    // Debit / Credit ids aren't in the dr / cr arrays (those slots are
    // [account, element, change]); the concept of "debit side" vs
    // "credit side" is implied by which array an entry lives in.
    //
    // Prefer looking up the Debit / Credit account_item ids from the
    // reference_items entry the view API tacks onto the response — that
    // way we compare ids exactly like Classification / Normal Balance
    // do, and any future backend renumbering flows through cleanly. If
    // reference_items doesn't include Debit / Credit yet (backend has
    // to add them), fall back to name comparison on the dropped chip's
    // name so the game still validates correctly today.
    const refItems =
      answerData?.find((a) => Array.isArray(a?.reference_items))?.reference_items || [];
    const debitId = refItems.find((r) => r.name === 'Debit')?.id;
    const creditId = refItems.find((r) => r.name === 'Credit')?.id;

    const isDR = answer.dr[1] === record.optionAccountItemId;
    const isCR = answer.cr[1] === record.optionAccountItemId;

    const isDebitChip = debitId != null
      ? record.transactionAccountItemId === debitId
      : record.transactionName === 'Debit';
    const isCreditChip = creditId != null
      ? record.transactionAccountItemId === creditId
      : record.transactionName === 'Credit';

    // Don't short-circuit on isDR — for compound entries where both
    // sides are on the same element (e.g. Assets goes down by paying
    // cash while another Asset goes up), isDR and isCR are both true
    // and we need to accept either chip type.
    if (isDR && isDebitChip) return true;
    if (isCR && isCreditChip) return true;
    return false;
  }

  if (type === 'core') {
    const drMatch =
      answer.dr[0] === record.coreAccountItemId &&
      answer.dr[1] === record.optionAccountItemId &&
      answer.dr[2] === record.changeAccountItemId;
    const crMatch =
      answer.cr[0] === record.coreAccountItemId &&
      answer.cr[1] === record.optionAccountItemId &&
      answer.cr[2] === record.changeAccountItemId;
    return drMatch || crMatch;
  }

  return false;
}

export function calculateScore({ timeElapsedSeconds, totalTries }) {
  const speedImpact = Math.max(0, 1 - SCORE_SPEED_FACTOR * timeElapsedSeconds);
  const accuracyImpact = totalTries === 1 ? 1 : Math.pow(SCORE_ACCURACY_FACTOR, totalTries);
  return Math.max(SCORE_MAX_POINTS * speedImpact * accuracyImpact, SCORE_MIN_POINTS);
}

export function getScoreAccuracy({ isCorrect, questionId, previouslyCorrectDecisions, type, optionAccountItemId }) {
  if (!isCorrect) return 0;

  const alreadyCorrect = previouslyCorrectDecisions.some(
    d => d.questionId === questionId && d.type === type && d.optionAccountItemId === optionAccountItemId
  );

  return alreadyCorrect ? null : 1;
}

const SESSION_STORAGE_PREFIX = 'be_session_';

export function saveSessionToStorage(gameSessionId, sessionData) {
  const key = `${SESSION_STORAGE_PREFIX}${gameSessionId}`;
  localStorage.setItem(key, JSON.stringify(sessionData));
}

export function loadSessionFromStorage(gameSessionId) {
  const key = `${SESSION_STORAGE_PREFIX}${gameSessionId}`;
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : null;
}

export function clearSessionFromStorage(gameSessionId) {
  const key = `${SESSION_STORAGE_PREFIX}${gameSessionId}`;
  localStorage.removeItem(key);
}

export function formatSecondsToTimeString(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function formatDecisionsForBulkPost(decisions, gameTimeElapsedSeconds) {
  // Fallback for legacy decisions that don't carry their own
  // game_time_elapsed; otherwise use the per-decision cumulative value
  // so the field grows monotonically across rows.
  const fallbackGameTimeElapsed = formatSecondsToTimeString(gameTimeElapsedSeconds);

  return decisions.map(d => ({
    game: d.game,
    game_session: d.game_session,
    question: d.question,
    selected_option: d.selected_option,
    item_option: d.item_option,
    selected_change: d.selected_change,
    selected_normal: d.selected_normal,
    box: d.box,
    group_id: d.group_id,
    game_time_elapsed: d.game_time_elapsed != null
      ? formatSecondsToTimeString(d.game_time_elapsed)
      : fallbackGameTimeElapsed,
    time_elapsed: formatSecondsToTimeString(d.time_elapsed)
  }));
}
